
import { useQuery } from "@tanstack/react-query";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";
import { ClientForm } from "./ClientForm";
import { ClientsTable } from "./ClientsTable";
import { useClients } from "@/hooks/useClients";
import { useNavigate, useLocation } from "react-router-dom";
import { Plus, Download, Edit3, Sparkles, ListFilter, Check, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { LoadingState } from '@/components/error-handling/LoadingState';
import { ErrorState } from "@/components/error-handling/ErrorState";
import { toast } from "sonner";

const ClientList = () => {
  const [showForm, setShowForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showOnlyHiring, setShowOnlyHiring] = useState(false);
  const { data: clients = [], isLoading, error, refetch } = useClients();
  const navigate = useNavigate();
  const location = useLocation();

  // Force refetch when navigating back to this component
  useEffect(() => {
    if (location.state?.refreshClients) {
      refetch();
      // Clear the state to prevent refetching on subsequent renders without explicit navigation action
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, refetch, navigate, location.pathname]);
  
  const handleClientUpdate = () => {
    refetch();
  };

  const filteredClientsByName = clients.filter(client => 
    client.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (client.contact_email && client.contact_email.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Remove the jobs filter since jobs property doesn't exist on Client
  const finalFilteredClients = filteredClientsByName;

  // Placeholder handlers for new Quick Action buttons
  const handleEnrichAllVisible = () => {
    toast.info("Enrich All Visible functionality will be implemented soon.");
    // Future: iterate finalFilteredClients, call enrichment service for each.
    // Consider batching or background processing for many clients.
  };

  const handleExportSelected = () => {
    toast.info("Export Selected functionality coming soon! Client selection checkboxes will be added to the table.");
    // Future: requires selected client IDs from table and CSV export logic.
  };

  const handleBulkStatusUpdate = () => {
    toast.info("Bulk Status Update functionality coming soon! This will allow updating multiple clients' status at once.");
    // Future: requires selected client IDs, a status selection mechanism, and an update mutation.
  };

  if (error) {
    return (
      <ErrorState 
        title="Error loading clients"
        description="Failed to load clients. Please try refreshing the page."
        onRetry={() => refetch()}
      />
    );
  }

  if (isLoading && clients.length === 0) { // Show loading state only if there are no clients yet
    return <LoadingState />;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="relative flex-1 w-full sm:w-auto">
          <Input
            placeholder="Search clients by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="max-w-sm w-full"
          />
        </div>
        <Button 
          onClick={() => setShowForm(true)}
          disabled={isLoading}
          className="flex items-center gap-1 self-start sm:self-auto"
        >
          <Plus className="h-4 w-4" />
          Add Client
        </Button>
      </div>

      {/* NEW: Quick Actions Bar */}
      <div className="flex flex-wrap items-center gap-2 p-3 border-y bg-muted/30 rounded-md">
          <span className="text-sm font-semibold mr-2 flex items-center text-muted-foreground">
            <ListFilter className="h-4 w-4 mr-1.5" />
            Quick Actions & Filters:
          </span>
          <Button variant="outline" size="sm" onClick={handleEnrichAllVisible} disabled={isLoading}>
              <Sparkles className="mr-1.5" /> Enrich Visible
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportSelected} disabled={isLoading}>
              <Download className="mr-1.5" /> Export Selected
          </Button>
          <Button variant="outline" size="sm" onClick={handleBulkStatusUpdate} disabled={isLoading}>
              <Edit3 className="mr-1.5" /> Bulk Update
          </Button>
          <Button
            variant={showOnlyHiring ? "secondary" : "outline"}
            size="sm"
            onClick={() => setShowOnlyHiring(!showOnlyHiring)}
            disabled={isLoading}
            className="whitespace-nowrap"
          >
            {showOnlyHiring ? <X className="mr-1.5" /> : <Check className="mr-1.5" />}
            {showOnlyHiring ? "All Clients" : "Filter Available"}
          </Button>
      </div>

      {showForm && (
        <ClientForm 
          onClose={() => setShowForm(false)} 
          onSuccess={() => {
            setShowForm(false);
            refetch(); // Ensure list is refreshed after adding a client
          }}
        />
      )}
      
      {isLoading && clients.length > 0 ? ( 
        <div className="text-sm text-muted-foreground">Updating list...</div>
      ) : (
        <ClientsTable 
          clients={finalFilteredClients} 
          onClientClick={clientId => navigate(`/clients/${clientId}`)} 
          onClientUpdate={handleClientUpdate}
        />
      )}
    </div>
  );
};

export default ClientList;
